import React, { useState } from 'react';
import { Copy, Share2, Check, Download } from 'lucide-react';

const FundDataExportSection = ({ product, category, currentCategory, formatReportPeriod, getLastUpdateDate, lastUpdated = null }) => {
  const [copyStatus, setCopyStatus] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // 🔧 שימוש בנתונים מוכנים מהמוצר במקום חישוב מחדש
  const stocksExp = product.exposures?.stocks || 0;
  const foreignExp = product.exposures?.foreign || 0;
  const currencyExp = product.exposures?.currency || 0;
  const domesticExp = foreignExp >= 100 ? 0 : Math.max(0, 100 - foreignExp);
  const illiquidAssets = product.performance?.liquidAssetsPercent ? 
    (100 - product.performance.liquidAssetsPercent) : 0;

  // 🔧 שימוש ישיר ברמת הסיכון המוכנה
  const riskData = product.riskLevel || {
    level: 0,
    name: 'לא ידוע'
  };

  // יצירת טקסט מפורט לייצוא
  const generateExportText = () => {
    const lines = [
      "📋 פרטי הקופה - יצוא מגמלנט AI",
      "",
      `📌 שם הקופה: ${product.name || 'לא זמין'}`,
      `🏢 גוף מנהל: ${product.company || 'לא זמין'}`,
      `📊 סוג מוצר: ${product.classification || currentCategory?.title || 'לא זמין'}`,
      `🎯 מסלול השקעה: ${product.specialization || product.fundInfo?.specialization || 'לא זמין'}`,
      `🔢 מספר באוצר: ${product.id || 'לא זמין'}`,
      `🆔 מספר גוף מנהל: ${product.managingCorpLegalId || 'לא זמין'}`,
      "",
      "💰 נתונים כספיים:",
      `   גודל הקופה: ${product.totalAssets ? 
        `${new Intl.NumberFormat('he-IL').format(product.totalAssets / 1000000)} מיליוני ש"ח` 
        : 'לא זמין'}`,
      `   הפקדות: ${product.deposits !== null ? `${new Intl.NumberFormat('he-IL').format(product.deposits)} מיליוני ש"ח` : 'לא זמין'}`,
      `   משיכות: ${product.withdrawals !== null ? `${new Intl.NumberFormat('he-IL').format(product.withdrawals)} מיליוני ש"ח` : 'לא זמין'}`,
      `   הפקדות נטו חודשיות: ${product.netMonthlyDeposits !== null ? `${new Intl.NumberFormat('he-IL').format(product.netMonthlyDeposits)} מיליוני ש"ח` : 'לא זמין'}`,
      "",
      "📈 תשואות:",
      `   תאריך נכונות הנתונים: ${lastUpdated}`,
      `   תשואה חודש ${lastUpdated}: ${product.returns?.month1 ? `${product.returns.month1.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה מצטברת 12 חודשים אחרונים: ${product.returns?.year1 ? `${product.returns.year1.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה מצטברת 2 שנים אחרונות: ${product.returns?.year2 ? `${product.returns.year2.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה מצטברת 3 שנים אחרונות: ${product.returns?.year3 ? `${product.returns.year3.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה מצטברת 4 שנים אחרונות: ${product.returns?.year4 ? `${product.returns.year4.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה מצטברת 5 שנים אחרונות: ${product.returns?.year5 ? `${product.returns.year5.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה מתחילת השנה: ${product.returns?.ytd ? `${product.returns.ytd.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה ממוצעת לכל שנה ב-2 שנים האחרונות: ${product.returns?.avgAnnual2Years ? `${product.returns.avgAnnual2Years.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה ממוצעת לכל שנה ב-3 שנים האחרונות: ${product.returns?.avgAnnual3Years ? `${product.returns.avgAnnual3Years.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה ממוצעת לכל שנה ב-4 שנים האחרונות: ${product.returns?.avgAnnual4Years ? `${product.returns.avgAnnual4Years.toFixed(2)}%` : 'לא זמין'}`,
      `   תשואה ממוצעת לכל שנה ב-5 שנים האחרונות: ${product.returns?.avgAnnual5Years ? `${product.returns.avgAnnual5Years.toFixed(2)}%` : 'לא זמין'}`,
      "",
      "🎯 חשיפות (במונחי דלתא):",
      `   חשיפה למניות: ${stocksExp.toFixed(2)}%`,
      `   חשיפה לחו"ל: ${foreignExp.toFixed(2)}%`,
      `   חשיפה לארץ: ${domesticExp.toFixed(2)}%`,
      `   חשיפה למט"ח: ${currencyExp.toFixed(2)}%`,
      `   נכסים לא סחירים: ${illiquidAssets.toFixed(2)}%`,
      "",
      "⚖️ מדדי סיכון:",
      `   רמת סיכון: ${riskData.level} מתוך 5 (${riskData.name || 'לא זמין'})`,
      `   אלפא שנתית: ${product.performance?.alpha ? product.performance.alpha.toFixed(2) : 'לא זמין'}`,
      `   יחס שארפ: ${product.performance?.sharpeRatio ? product.performance.sharpeRatio.toFixed(2) : 'לא זמין'}`,
      `   סטיית תקן: ${product.performance?.standardDeviation ? product.performance.standardDeviation.toFixed(1) + '%' : 'לא זמין'}`,
      `   יחס נזילות: ${product.performance?.liquidAssetsPercent ? product.performance.liquidAssetsPercent.toFixed(1) + '%' : 'לא זמין'}`,
      "",
      "📅 תאריכים:",
      `   תאריך הקמה: ${product.inceptionDate || product.fundInfo?.inceptionDate || 'לא זמין'}`,
      `   תקופת דיווח אחרונה: ${product.reportPeriod ? formatReportPeriod(product.reportPeriod) : 'לא זמין'}`,
      `   תאריך עדכון אחרון: ${product.lastUpdate ? new Date(product.lastUpdate).toLocaleDateString('he-IL') : 'לא זמין'}`,
      `   תאריך נתונים נוכחי: ${product.currentDate ? new Date(product.currentDate).toLocaleDateString('he-IL') : 'לא זמין'}`,
      "",
      "🔧 מידע טכני:",
      `   מקור נתונים: ${product.sourceLabel || product.source || 'לא זמין'}`,
      `   נתונים אמיתיים: ${product.isRealData ? 'כן' : 'לא'}`,
      `   נטען מ: ${product.loadedFrom || 'לא זמין'}`,
      `   אוכלוסיית יעד: ${product.targetPopulation || product.fundInfo?.targetPopulation || 'לא זמין'}`,
      `   התאמה אקטוארית: ${product.actuarialAdjustment !== null ? product.actuarialAdjustment : 'לא רלוונטי'}`,
      `   העברות פנימיות: ${product.internalTransfers !== null ? `${new Intl.NumberFormat('he-IL').format(product.internalTransfers)} מיליון ש"ח` : 'לא זמין'}`,
      "",
      "📱 יוצא מאתר גמל נט AI - המדריך החכם לעולם הפנסיה והחיסכון",
      `🔗 קישור למוצר: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `⏰ תאריך יצוא: ${new Date().toLocaleDateString('he-IL')} ${new Date().toLocaleTimeString('he-IL')}`,
      "",
      "⚠️ הבהרה:",
    "📊 המידע באתר גמל נט AI נועד להנגיש תובנות והשוואות, אך אינו מהווה ייעוץ פנסיוני, פיננסי או השקעות.",
  "📄✅ כל החלטה צריכה להיעשות תוך התאמה אישית ובליווי בעל רישיון מוסמך בלבד.",
  "⏳ תשואות עבר אינן מעידות בהכרח על ביצועים עתידיים, ואין לראות בנתונים באתר התחייבות או המלצה.",
  "🧠 אנו עושים מאמץ כנה בשילוב מערכות בינה מלאכותית מתקדמות לספק מידע מדויק, אך האחריות לשימוש בו ולפרשנותו היא בידי המשתמש בלבד.",
      "",
    ];

    return lines.join('\n');
  };

  const handleCopyAll = async () => {
    setIsExporting(true);
    try {
      const exportText = generateExportText();
      await navigator.clipboard.writeText(exportText);
      setCopyStatus('copied');
      
      setTimeout(() => {
        setCopyStatus('');
      }, 3000);
    } catch (error) {
      console.error('Copy failed:', error);
      setCopyStatus('error');
      setTimeout(() => {
        setCopyStatus('');
      }, 3000);
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        const exportText = generateExportText();
        await navigator.share({
          title: `פרטי ${product?.name || 'הקופה'}`,
          text: exportText
        });
      } catch (error) {
        console.log('שיתוף בוטל');
      }
    } else {
      // fallback להעתקה
      handleCopyAll();
    }
  };

  return (
    <div className="group relative">
      {/* רקע עם גלאסמורפיזם */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 backdrop-blur-xl rounded-3xl border border-white/30 shadow-2xl shadow-blue-900/10"></div>
      
      {/* אפקט זוהר */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
      
      <div className="relative p-6">
        {/* כותרת מינימליסטית */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl opacity-20 blur-lg"></div>
          </div>
          <div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent">
              ייצוא נתונים
            </h3>
            <p className="text-gray-500 text-sm font-medium mt-1">
              כל פרטי הקופה בלחיצה אחת
            </p>
          </div>
        </div>

        {/* כפתורים מתקדמים */}
        <div className="flex gap-3">
          {/* כפתור העתקה ראשי */}
          <button
            onClick={handleCopyAll}
            disabled={isExporting}
            className={`
              relative flex-1 group/btn overflow-hidden
              px-5 py-3 rounded-xl font-medium text-base
              transition-all duration-500 ease-out
              transform hover:scale-[1.02] active:scale-[0.98]
              ${copyStatus === 'copied'
                ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/25'
                : copyStatus === 'error'
                ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-500/25'
                : 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white shadow-lg shadow-blue-600/25 hover:shadow-blue-600/35'
              }
            `}
          >
            {/* אפקט זוהר פנימי */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"></div>
            
            {/* תוכן הכפתור */}
            <div className="relative flex items-center justify-center gap-2">
              {isExporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span className="text-sm sm:text-base">מייצא...</span>
                </>
              ) : copyStatus === 'copied' ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm sm:text-base">הועתק!</span>
                </>
              ) : copyStatus === 'error' ? (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-sm sm:text-base">נסה שוב</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-sm sm:text-base">העתק נתונים</span>
                </>
              )}
            </div>
          </button>

          {/* כפתור שיתוף */}
          <button
            onClick={handleShare}
            className="relative group/share overflow-hidden
                     px-5 py-3 rounded-xl font-medium text-base
                     bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600
                     text-white shadow-lg shadow-indigo-600/25
                     transition-all duration-500 ease-out
                     transform hover:scale-[1.02] active:scale-[0.98]
                     hover:shadow-indigo-600/35"
          >
            {/* אפקט זוהר פנימי */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/share:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative flex items-center justify-center gap-2">
              <Share2 className="w-4 h-4" />
              <span className="text-sm sm:text-base">שתף</span>
            </div>
          </button>
        </div>

        {/* הודעת הצלחה מתקדמת */}
        {copyStatus === 'copied' && (
          <div className="mt-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-xl"></div>
            <div className="relative p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-emerald-200/50">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <div>
                  <p className="text-emerald-800 font-medium text-sm">
                    כל נתוני הקופה הועתקו בהצלחה
                  </p>
                  <p className="text-emerald-700 text-xs mt-0.5">
                    ניתן להדביק בכל יישום - WhatsApp, מייל או מסמך
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundDataExportSection;